import React, {FC} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelErrorModel} from '../../../../../libs/shared/core/src/models/api/channelErrorModel';
import {ChannelModel} from '../../../../../libs/shared/core/src/models/api/channelModel';
import {Banner, BannerTypesEnum} from '../../../../../libs/shared/ui/src/components/banners/banner';
import {BannerAction} from '../../../../../libs/shared/ui/src/components/banners/bannerAction';
import {BannerCloseAction} from '../../../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerContent} from '../../../../../libs/shared/ui/src/components/banners/bannerContent';
import {BannerContentTitle} from '../../../../../libs/shared/ui/src/components/banners/bannerContentTitle';
import {BannerFooterActions} from '../../../../../libs/shared/ui/src/components/banners/bannerFooterActions';
import {BannerIcon} from '../../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../../../libs/shared/ui/src/components/banners/bannersContext';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ChannelErrorsEnum} from '../../../../helpers/models/channelHelpers';

/*
 * Props.
 */

interface ChannelErrorBannerProps {
  channel: ChannelModel;
  onOpenSettingsClick: (() => void) | undefined;
  onLinkClick: () => void;
  onDismissClick: () => void;
}

/*
 * Component.
 */

export const ChannelErrorBanner: FC<ChannelErrorBannerProps> = (props) => {
  const theme = useUserTheme();
  // We need a channel with an error at this point.
  const {channel} = props;
  const channelError = channel.error;
  if (!channelError) {
    return null;
  }

  // If the channel access is being denied, we want to only allow temporary dismissal
  // and make it more obvious to the user that the banner may come back
  const isChannelDeniedError = channelError.type === ChannelErrorsEnum.DENIED;

  return (
    <Banner
      bannerType={BannerTypesEnum.MULTILINE}
      color={theme.banner.warningBackground}
      priority={BannerPrioritiesEnum.ERROR}
    >
      {!isChannelDeniedError && <BannerCloseAction onClick={props.onDismissClick} />}
      <BannerIcon iconName="warningFilledSmall" />
      <BannerContent>
        <BannerContentTitle>{channel.displayName}</BannerContentTitle>
        {channelError.description}
      </BannerContent>
      <BannerFooterActions>
        {renderLink(props, channelError)}
        {isChannelDeniedError && (
          <BannerAction onClick={props.onDismissClick}>
            <Trans id="channel-error-notnow">Not Now</Trans>
          </BannerAction>
        )}
      </BannerFooterActions>
    </Banner>
  );
};

function renderLink(props: ChannelErrorBannerProps, channelError: ChannelErrorModel) {
  const {onLinkClick, onOpenSettingsClick} = props;

  if (channelError.linkCaption) {
    return <BannerAction onClick={onLinkClick}>{channelError.linkCaption}</BannerAction>;
  }

  if (!onOpenSettingsClick) {
    return null;
  }

  return (
    <BannerAction onClick={onOpenSettingsClick}>
      <Trans id="channel-error-settings">View settings</Trans>
    </BannerAction>
  );
}
